import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import { Check, ChevronRight } from 'lucide-react'
import Modal from 'ui/Modal'
import UserSettingsAccountPassword from './UserSettingsAccountPassword'
import UserSettingsAccountDeactivate from './UserSettingsAccountDeactivate'
import UserSettingsAccountDelete from './UserSettingsAccountDelete'
import UserSettingsAccountGoogleConnect from './UserSettingsAccountGoogleConnect'
import UserSettingsAccountGoogleDisconnect from './UserSettingsAccountGoogleDisconnect'
import UserSettingsAccountSessions from './UserSettingsAccountSessions'
import EmailVerificationModal, { EMAIL_VERIFICATION_FORM_ID } from 'components/Auth/EmailVerificationModal'
import FormActions from 'ui/FormActions'
import { getAuthUiStrings, resolveAuthLocale } from 'i18n/authUi'
import { getAccountSettingsUiStrings, resolveAccountSettingsLocale } from 'i18n/accountSettingsUi'
import { updateOtpLogin } from 'util/userUtils'
import LoadingSkeleton from 'ui/LoadingSkeleton'

const UserSettingsAccount: React.FC = () => {
    const { currentUser, awaitingUser, refreshUser } = useUser()
    const authUi = getAuthUiStrings(resolveAuthLocale())
    const ui = getAccountSettingsUiStrings(resolveAccountSettingsLocale())
    const [openDeactivateModal, setOpenDeactivateModal] = useState<boolean>(false)
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
    const [openPasswordModal, setOpenPasswordModal] = useState<boolean>(false)
    const [openEmailVerificationModal, setOpenEmailVerificationModal] = useState<boolean>(false)
    const [openGoogleConnectModal, setOpenGoogleConnectModal] = useState<boolean>(false)
    const [openGoogleDisconnectModal, setOpenGoogleDisconnectModal] = useState<boolean>(false)
    const [otpLogin, setOtpLogin] = useState<boolean>(currentUser?.properties.preferences_auth_otp_login ? currentUser?.properties.preferences_auth_otp_login : false)
    const [emailVerificationState, setEmailVerificationState] = useState<{
        isSubmitting: boolean;
        isButtonDisabled: boolean;
        buttonLabel: string;
    }>({
        isSubmitting: false,
        isButtonDisabled: true,
        buttonLabel: authUi.emailVerificationContinue,
    })

    // Auth-unauth redirect is handled by the parent `UserSettings`
    // route via `RequireAuth`; this component only initializes its
    // local state from `currentUser`.
    useEffect(() => {
        setOtpLogin(currentUser?.properties.preferences_auth_otp_login ? currentUser?.properties.preferences_auth_otp_login : false)
    }, [currentUser])

    const handleOtpChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const response = await updateOtpLogin(e.target.checked)
            setOtpLogin(response.data.properties.preferences_auth_otp_login)
            if (currentUser) {
                currentUser.properties.preferences_auth_otp_login = response.data.properties.preferences_auth_otp_login
            }
        } catch (error) {
            console.error('Error updating OTP login preference:', error)
        }
    }

    const handleCloseEmailVerificationModal = () => {
        setOpenEmailVerificationModal(false)
        setEmailVerificationState({
            isSubmitting: false,
            isButtonDisabled: true,
            buttonLabel: authUi.emailVerificationContinue,
        })
    }

    if (!currentUser || awaitingUser) {
        return (
            <LoadingSkeleton variant="settings" count={3} className=""/>
        )
    }

    const googleConnection = currentUser?.auth_connections?.find((connection) => connection.provider === "google")
    const hasPassword = currentUser?.has_password === true || currentUser?.auth_type === "local"
    const isGoogleConnected = googleConnection?.linked === true
    const isEmailVerified = currentUser?.properties?.email_verified === true
    const canVerifyEmail = hasPassword && !isEmailVerified
    const emailVerificationFooter = (
        <FormActions
            formId={EMAIL_VERIFICATION_FORM_ID}
            submitLabel={emailVerificationState.buttonLabel}
            cancelLabel={ui.modalCancel}
            submitDisabled={emailVerificationState.isButtonDisabled}
            submitting={emailVerificationState.isSubmitting}
            onCancel={handleCloseEmailVerificationModal}
        />
    )

    return (
        <div className="p-2">
            <div className="space-y-2">

                {/* General Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-lg">{ui.generalSection}</label>
                    {hasPassword && (
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenPasswordModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.passwordTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">{ui.passwordDescription}</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                    )}
                    {hasPassword && (
                    <div
                        className={`group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md ${
                            canVerifyEmail
                                ? 'cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover'
                                : 'opacity-70'
                        }`}
                        role={canVerifyEmail ? 'button' : undefined}
                        tabIndex={canVerifyEmail ? 0 : -1}
                        aria-disabled={canVerifyEmail ? undefined : true}
                        onClick={canVerifyEmail ? () => setOpenEmailVerificationModal(true) : undefined}
                        onKeyDown={canVerifyEmail ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                setOpenEmailVerificationModal(true)
                            }
                        } : undefined}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.emailVerificationTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                {isEmailVerified ? ui.emailVerificationVerifiedDescription : ui.emailVerificationUnverifiedDescription}
                            </p>
                        </div>
                        {isEmailVerified ? (
                            <Check className="size-5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                        ) : (
                            <ChevronRight className="inline-arrow-icon" />
                        )}
                    </div>
                    )}
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md"
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.otpLoginTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">{ui.otpLoginDescription}</p>
                        </div>
                            <input
                                type="checkbox"
                                id="otp_enabled"
                                checked={otpLogin}
                                onChange={handleOtpChange}
                                className="toggle-switch-input col-span-1 flex-shrink-0"
                            />
                    </div>
                </div>

                <div className="form-control space-y-2">
                    <label className="form-label text-lg">{ui.authorizationSection}</label>
                    <div className="flex items-center justify-between gap-4 rounded-md bg-eoe-bg-hover p-3 dark:bg-eoe-dark-bg-hover">
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.authorizationGoogleTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                {isGoogleConnected ? ui.authorizationGoogleConnectedDescription : ui.authorizationGoogleDisconnectedDescription}
                            </p>
                            <span className="text-xs font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                {isGoogleConnected ? ui.authorizationStatusConnected : ui.authorizationStatusNotConnected}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn-secondary px-4 py-2"
                            onClick={() => {
                                if (isGoogleConnected) {
                                    setOpenGoogleDisconnectModal(true)
                                } else {
                                    setOpenGoogleConnectModal(true)
                                }
                            }}
                        >
                            {isGoogleConnected ? ui.authorizationDisconnectButton : ui.authorizationConnectButton}
                        </button>
                    </div>
                </div>

                <UserSettingsAccountSessions />

                {/* Advanced Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-lg">{ui.advancedSection}</label>
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenDeactivateModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.deactivateTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">{ui.deactivateDescription}</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                    <div
                        className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md cursor-pointer hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        onClick={() => setOpenDeleteModal(true)}
                    >
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text">{ui.deleteTitle}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">{ui.deleteDescription}</p>
                        </div>
                        <ChevronRight className="inline-arrow-icon" />
                    </div>
                </div>
            </div>

            {/* Password Change Modal */}
            <UserSettingsAccountPassword
                isOpen={openPasswordModal}
                onClose={() => setOpenPasswordModal(false)}
            />

            <UserSettingsAccountGoogleConnect
                isOpen={openGoogleConnectModal}
                onClose={() => setOpenGoogleConnectModal(false)}
                onSuccess={refreshUser}
            />

            <UserSettingsAccountGoogleDisconnect
                hasPassword={hasPassword}
                isOpen={openGoogleDisconnectModal}
                onClose={() => setOpenGoogleDisconnectModal(false)}
                onSuccess={refreshUser}
            />

            <Modal
                isOpen={openEmailVerificationModal}
                onClose={handleCloseEmailVerificationModal}
                title={ui.verifyEmailModalTitle}
                footer={emailVerificationFooter}
                stickyFooter
                className="text-center"
                formId={EMAIL_VERIFICATION_FORM_ID}
            >
                <EmailVerificationModal
                    onClose={handleCloseEmailVerificationModal}
                    onStateChange={setEmailVerificationState}
                    email={currentUser.email}
                    requestCodeOnOpen
                />
            </Modal>

            {/* Deactivate Modal */}
            <UserSettingsAccountDeactivate
                isOpen={openDeactivateModal}
                onClose={() => setOpenDeactivateModal(false)}
            />

            {/* Delete Modal */}
            <UserSettingsAccountDelete
                isOpen={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
            />
        </div>
    )
}

export default UserSettingsAccount
